import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { QueuedSendResult } from '../../shared/api';
import { MAIL_RATE_CENTS } from '../../shared/api';
import SignaturePad, { type SignaturePadHandle } from '../../components/SignaturePad';
import { Banner, Button, DataTable, Field, Input, Pill, Td, Textarea } from '../../components/ui';
import { api, ApiError } from '../../lib/api';
import { centsToDollars, fmtDateTime } from '../../lib/format';
import { useSession } from '../../lib/session';
import type { TabProps } from './types';

/** Seconds left in the undo window, recomputed every second. */
function useCountdown(firesAt: string | null): number {
  const [left, setLeft] = useState(() => remaining(firesAt));
  useEffect(() => {
    setLeft(remaining(firesAt));
    if (!firesAt) return;
    const t = setInterval(() => setLeft(remaining(firesAt)), 1000);
    return () => clearInterval(t);
  }, [firesAt]);
  return left;
}

const remaining = (firesAt: string | null) =>
  firesAt ? Math.max(0, Math.ceil((new Date(firesAt).getTime() - Date.now()) / 1000)) : 0;

export default function NooTab({ job, reload, goToTab }: TabProps) {
  const { contractor, can, refresh, platformMailingEnabled } = useSession();
  const sigRef = useRef<SignaturePadHandle>(null);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState<QueuedSendResult | null>(null);

  const firesAt = queued?.firesAt ?? job.queuedSend?.firesAt ?? null;
  const attemptLeft = useCountdown(firesAt);
  const pending = !!firesAt && attemptLeft > 0;

  const balance = contractor?.mailBalanceCents ?? 0;
  const costCents = job.nooRecipientCount * MAIL_RATE_CENTS;
  const funded = balance >= costCents && costCents > 0;
  const acknowledged = !!contractor?.mailAckAt;
  const maySend = can('manager');
  const alreadySent = !!job.nooSentAt;

  const send = async () => {
    setError(null);
    const signatureImage = sigRef.current?.toDataUrl();
    if (!signatureImage) return setError('A signature is required before certified mail goes out.');
    setBusy(true);
    try {
      const result = await api.post<QueuedSendResult>(`/api/jobs/${job.id}/noo`, {
        descriptionOfWork: description,
        contractAmount: amount ? Number(amount) : undefined,
        signatureImage,
      });
      setQueued(result);
      await refresh();
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not queue the Notice to Owner');
    } finally {
      setBusy(false);
    }
  };

  const cancel = async () => {
    setBusy(true);
    try {
      await api.post(`/api/jobs/${job.id}/cancel-send`);
      setQueued(null);
      await refresh();
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not cancel the send');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 text-sm font-semibold text-ink">Recipients</h3>
        <DataTable
          head={['Recipient', 'Role', 'Address', 'Tracking', 'Status']}
          empty={`No letters yet. This NTO will go to ${job.nooRecipientCount} recipient${job.nooRecipientCount === 1 ? '' : 's'}.`}
        >
          {job.recipients.map((r) => (
            <tr key={r.id} className="border-b border-border/60">
              <Td className="font-medium">{r.name}</Td>
              <Td className="text-muted">{r.recipientType.replace('_', ' ')}</Td>
              <Td className="text-xs text-muted">{r.address}</Td>
              <Td className="font-mono text-xs text-muted">{r.trackingNumber ?? '—'}</Td>
              <Td>
                {r.deliveredAt ? (
                  <Pill tone="ok">Delivered {fmtDateTime(r.deliveredAt)}</Pill>
                ) : r.sentAt ? (
                  <Pill tone="watch">Sent {fmtDateTime(r.sentAt)}</Pill>
                ) : (
                  <Pill>Pending</Pill>
                )}
              </Td>
            </tr>
          ))}
        </DataTable>
      </div>

      {pending && (
        <Banner tone="watch" title={`Certified mail goes out in ${attemptLeft}s`}>
          <p>Nothing has been mailed yet. Cancel now and the hold is refunded in full.</p>
          <Button variant="danger" onClick={cancel} disabled={busy} className="mt-3">
            Cancel send ({attemptLeft}s)
          </Button>
        </Banner>
      )}

      {alreadySent && !pending && (
        <Banner tone="ok" title="Notice to Owner sent">
          Mailed {fmtDateTime(job.nooSentAt)}. Delivery is tracked per recipient above.
        </Banner>
      )}

      {!alreadySent && !pending && !platformMailingEnabled && (
        <Banner tone="ok" title="During early access, you mail the NTO yourself">
          <p>
            LienGuardian prepares the Notice to Owner; you send it by USPS Certified Mail and upload the receipt.
            Nothing is charged.
          </p>
          <Button className="mt-3" onClick={() => goToTab?.('mailing')}>
            Prepare it on Self-Mail →
          </Button>
        </Banner>
      )}

      {!alreadySent && !pending && platformMailingEnabled && (
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-ink">Send the Notice to Owner</h3>

          {!maySend && (
            <Banner tone="watch" title="Your seat cannot send certified mail">
              Sending an NTO needs a manager or admin seat. Ask the office to send it, or request it from the mobile app.
            </Banner>
          )}

          {maySend && !acknowledged && (
            <Banner tone="urgent" title="Certified mail acknowledgment required">
              <Link to="/wallet" className="underline">
                Sign the acknowledgment on the wallet page
              </Link>{' '}
              before any certified mail can leave.
            </Banner>
          )}

          {maySend && acknowledged && !funded && (
            <Banner tone="urgent" title="Not enough certified mail balance">
              This NTO needs {centsToDollars(costCents)} for {job.nooRecipientCount} letter
              {job.nooRecipientCount === 1 ? '' : 's'}; your balance is {centsToDollars(balance)}.{' '}
              <Link to="/wallet" className="underline">
                Add funds
              </Link>
              .
            </Banner>
          )}

          {error && <Banner tone="urgent">{error}</Banner>}

          {maySend && acknowledged && funded && (
            <div className="space-y-4">
              <Field label="Description of work" hint="Printed on the notice, per Fla. Stat. 713.06.">
                <Textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Electrical rough-in and fixture installation"
                />
              </Field>
              <Field label="Contract amount (optional)" hint="Leave blank to use the job's contract amount.">
                <Input type="number" min="1" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} />
              </Field>
              <SignaturePad ref={sigRef} label="Signature" />
              <div className="flex items-center justify-between gap-4 border-t border-border pt-4">
                <p className="text-xs text-dim">
                  {job.nooRecipientCount} certified letter{job.nooRecipientCount === 1 ? '' : 's'} ·{' '}
                  {centsToDollars(costCents)} · 60 second undo window
                </p>
                <Button onClick={send} disabled={busy || description.trim().length < 3}>
                  {busy ? 'Queueing…' : 'Send Notice to Owner'}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
